/**
 * The picker's swatch: the actual vehicle, wearing the actual paint.
 *
 * A livery is a wrap on a body, and the two questions a rider is really asking — what does
 * this look like, and what does it look like *on the bus* — are not answered by a flat side
 * elevation. So a swatch here is a **render of the same glTF the map draws**, patched by the
 * same `liveryScenegraph` cache, lit by the same IBL environment, captured once to a PNG.
 * Adding a livery is still one entry in `liveries.ts`; its picture comes free and cannot be
 * a picture of something the map does not draw.
 *
 * ── How it works, and the traps ──────────────────────────────────────────────────────
 *
 * Nothing here reuses the map's renderer: the map's deck instance is a MapboxOverlay welded
 * to a MapLibre camera, and a swatch needs a fixed three-quarter view of one object at the
 * origin. So this opens its own headless `Deck` on a detached canvas — one for the whole
 * batch, torn down the moment the queue drains, because a WebGL context is a scarce resource
 * and the map is already holding one.
 *
 *   • **Renders are serialised.** Nine previews in nine contexts would be nine devices and
 *     nine copies of the PBR environment; through one studio it is one of each, and the
 *     capture handshake ("draw, then read the pixels") only has one frame to reason about.
 *   • **The scenegraph is AWAITED**, via `liveryScenegraphReady`. The map's sync path draws
 *     the plain model until the patch lands and picks the paint up next frame; a still image
 *     has no next frame, and would keep the unpainted capture forever.
 *   • **The tier is whichever one carries a real unwrap, and that differs per fleet.** L1 is
 *     the tier to want — a mid-zoom mesh, ample for a ~150 px tile, and its patched glTF is
 *     shared with the map rather than being a tenth model held in memory. The R160's L1 has
 *     the panel. **The shipped bus L1 does not** (see `FLEET.bus.model`), so the bus previews
 *     from L0. L2 is never either fleet's: it is one merged shell with its UVs stripped.
 *   • **It never rejects and never blocks.** A preview is decoration: no WebGL, a failed
 *     load, a device that never draws — all of them fall back to the flat side elevation
 *     below, which is drawn synchronously so a tile is never empty while the model loads.
 */

import {
  AmbientLight,
  COORDINATE_SYSTEM,
  Deck,
  DirectionalLight,
  LightingEffect,
  OrbitView,
} from '@deck.gl/core'
import { ScenegraphLayer } from '@deck.gl/mesh-layers'
import { load } from '@loaders.gl/core'
import { GLTFLoader } from '@loaders.gl/gltf'
import { VENDORED_GLB_LOAD_OPTIONS } from './gltfLoadOptions'
import {
  AS_BUILT_FLANK,
  LIVERY_CANVAS_W,
  LIVERY_FLANK_H,
  liveryPaint,
  liveryPanelAspect,
  liveryPanelBox,
  type LiveryBody,
  type LiveryId,
  type LiveryVehicle,
} from './liveries'
import { liveryScenegraphReady, type LiveryTarget } from './liveryScenegraph'
import { r160PbrEnvironment } from './pbrEnvironment'
import {
  R160_CAB_FORWARD_YAW,
  R160_FAR_SHELL_MATERIAL,
  R160_LIVERY_SURFACES,
  R160_MODELS,
  r160TierUsesPbr,
} from './r160Model'
import {
  XCEL40_FAR_SHELL_MATERIAL,
  XCEL40_LIVERY_SURFACES,
  XCEL40_MODELS,
  XCEL40_SHELL_MATERIAL,
  xcel40TierUsesPbr,
} from './xcel40Model'

/**
 * The tile's shape, and ONE shape for both fleets — a bus and a subway car are framed to
 * fill the same box rather than being drawn to a common scale, because a picker compares
 * paint, not vehicle sizes, and two aspects would make the grid jump as the 3D renders
 * replace the flat placeholders under them.
 *
 * 2.8:1 is what the camera below actually needs: at a shallow three-quarter view a subway
 * car projects about 3.3:1 and a bus about 2.7:1, so this fills one fleet's width and the
 * other's height with a little air either way.
 */
export const LIVERY_PREVIEW_ASPECT = 2.8

/** CSS pixels. Deck renders at `useDevicePixels`× this, so the capture is a retina image. */
const PREVIEW_W = 320
const PREVIEW_H = Math.round(PREVIEW_W / LIVERY_PREVIEW_ASPECT)

/**
 * The camera, in degrees: yaw about the vehicle's own axis, then elevation.
 *
 * Shallow on purpose. A 45° hero angle is a taller picture — it spends the tile's height on
 * perspective rather than on the flank, and the flank is where a livery lives. At 22°/17°
 * the roof is plainly visible (which matters: this map looks straight down, so `shellColor`
 * is what a rider mostly sees) while the side elevation still reads end to end.
 */
const PREVIEW_ORBIT_DEG = -22
const PREVIEW_ELEVATION_DEG = 17

/**
 * What the panel box does not cover, as one factor.
 *
 * Two things: the panel stops ~3% short of both bodies, and it has no beam at all, while a
 * three-quarter view is widened by it (a ~3 m body across, at 22°, adds ~1.1 m to a 17.7 m
 * car — 6%). The rest is air, so a vehicle does not touch the tile's edges.
 */
const FRAME_MARGIN = 1.14

type FleetSpec = {
  body: LiveryBody
  /** The tier to render — see the header, and `FLEET.bus` on why the two fleets differ. */
  model: string
  target: LiveryTarget
  usesPbr: boolean
  /** Yaw applied on top of the camera, to put the vehicle's FRONT at the near end. */
  yaw: number
}

/**
 * Which body stands for its fleet, and how it is dressed.
 *
 * The subway's two bodies wear different artwork under a scheme that hands them (`mta` rakes
 * the A car's band off on a pillar and flashes the cab), and the A car is what a rider is
 * looking at when a train pulls in, so it is the honest advertisement for the paint. Its
 * cab is modelled at −X, hence the same `R160_CAB_FORWARD_YAW` the map applies to a
 * cab-forward car: without it the preview would show the train reversing out of the tile.
 */
const FLEET: Record<LiveryVehicle, FleetSpec> = {
  subway: {
    body: 'r160-a',
    model: R160_MODELS.A.L1,
    target: {
      body: 'r160-a',
      surfaceMaterials: R160_LIVERY_SURFACES,
      farShellMaterial: R160_FAR_SHELL_MATERIAL,
    },
    usesPbr: r160TierUsesPbr('L1'),
    yaw: R160_CAB_FORWARD_YAW,
  },
  bus: {
    body: 'xcel40',
    // ⚠️ L0, where the subway takes L1, and the reason is a defect in the shipped asset, not
    // a rendering preference: **`BUS-XCEL-40_L1.glb` has no livery unwrap.** Every material
    // the exporter derives from the body — `MAT_LIVERY`, the door and glass panels, the caps,
    // even the blockout — has its whole TEXCOORD_0 collapsed onto the single texel (u 0, v 1);
    // only the wheels, which come from elsewhere in the exporter, kept theirs. So a livery
    // applied at that tier is not artwork at all, it is one pixel of the sheet stretched over
    // the bus: `mta` renders as flat MTA yellow (that texel), `minion` as flat navy, `wrap` as
    // flat teal. Measured off the GLBs' own decoded accessors, both tiers, both fleets — L0's
    // panel spans u 0…1 and is correct.
    //
    // This is upstream in `mta-assets`' bus exporter (`assign_livery_panel`) and it degrades
    // the MAP too, at every mid zoom `xcel40LodForZoom` answers 'L1'. Fixing it there means a
    // re-export and a `?rev=` bump on `XCEL40_MODELS`; when that lands, this goes back to L1
    // and the header's bullet with it. Until then a swatch has to be drawn from a tier that
    // can actually carry the paint — a picker whose four bus tiles are four flat colours is
    // not a picker — and the cost is one 122 KB model instead of 46 KB, loaded once, only
    // while the sheet is open.
    model: XCEL40_MODELS.L0,
    target: {
      body: 'xcel40',
      surfaceMaterials: XCEL40_LIVERY_SURFACES,
      farShellMaterial: XCEL40_FAR_SHELL_MATERIAL,
      // The roof, which on this map is the surface a vehicle mostly shows — and the reason a
      // preview from above the beltline is worth rendering at all.
      shellMaterial: XCEL40_SHELL_MATERIAL,
    },
    usesPbr: xcel40TierUsesPbr('L0'),
    yaw: 0,
  },
}

/**
 * Deck's own default lighting is tuned for a map read from directly above; these are the same
 * three lights aimed at an object seen from the side, so the flank is the lit surface rather
 * than the roof. Built once — a `LightingEffect` is device-independent.
 */
const PREVIEW_LIGHTING = new LightingEffect({
  ambient: new AmbientLight({ color: [255, 255, 255], intensity: 1.3 }),
  key: new DirectionalLight({ color: [255, 255, 255], intensity: 1.5, direction: [-1, -0.6, -1.4] }),
  fill: new DirectionalLight({ color: [255, 255, 255], intensity: 0.6, direction: [1.2, 0.8, -0.6] }),
})

const rad = (deg: number) => (deg * Math.PI) / 180

/** Where to stand so this body fills the tile. Derived, never a hand-tuned zoom. */
function framing(body: LiveryBody): { target: [number, number, number]; zoom: number } {
  const { x0, x1, z1 } = liveryPanelBox(body)
  const length = x1 - x0
  const height = z1
  const orbit = Math.abs(rad(PREVIEW_ORBIT_DEG))
  const elevation = rad(PREVIEW_ELEVATION_DEG)
  // The body's own box, projected: length foreshortens with the yaw, and what the yaw turns
  // away from the camera comes back as height once the camera is raised.
  const projectedW = length * Math.cos(orbit) * FRAME_MARGIN
  const projectedH =
    (length * Math.sin(orbit) * Math.sin(elevation) + height * Math.cos(elevation)) * FRAME_MARGIN
  // Orthographic: 2^zoom is pixels per metre. Fit the tighter axis.
  const pxPerMetre = Math.min(PREVIEW_W / projectedW, PREVIEW_H / projectedH)
  return { target: [(x0 + x1) / 2, 0, height / 2], zoom: Math.log2(pxPerMetre) }
}

// ── The studio ───────────────────────────────────────────────────────────────────────

type Studio = {
  /** Generic in its view: an OrbitView deck takes orbit view state, not the map's. */
  deck: Deck<OrbitView>
  canvas: HTMLCanvasElement
  /** Resolved by the next `onAfterRender`; see `drawnFrame`. */
  onFrame: (() => void) | null
}

let studio: Studio | null = null
/** Renders run one at a time, through this. */
let queue: Promise<void> = Promise.resolve()
let outstanding = 0

/** The minimum of a document this needs, so the attachment above can be tested without one. */
type CanvasHost = {
  createElement(tag: 'canvas'): HTMLCanvasElement
  body: { appendChild(node: HTMLCanvasElement): unknown }
}

/**
 * The studio's canvas, sized and **attached to the document**.
 *
 * ⚠️ Attached is the load-bearing word, and it is why this is its own function with its own
 * test. Deck takes its own size from `canvas.clientWidth/clientHeight` (`_updateCanvasSize`)
 * — the `width`/`height` props it is given only set the CSS size — and an element outside the
 * document is never laid out, so `clientWidth` reads 0. Deck's `?? canvas.width` fallback is
 * there for `OffscreenCanvas`, where those are `undefined`; it does not catch 0.
 *
 * Nothing about that failure looks like a failure. A 0×0 deck leaves the ViewManager with no
 * viewport at all, so the model still loads, frames still fire, `onAfterRender` still
 * resolves — and every pixel captured is transparent. The whole picker quietly kept its flat
 * elevations with nothing logged anywhere.
 *
 * So the studio is parked OFF-SCREEN rather than detached. `display: none` would be the
 * identical bug (no layout, no `clientWidth`); `position: fixed` keeps it out of flow, so it
 * cannot extend the page's scroll extents for the seconds it is open.
 */
export function createStudioCanvas(host: CanvasHost): HTMLCanvasElement {
  const canvas = host.createElement('canvas')
  canvas.width = PREVIEW_W
  canvas.height = PREVIEW_H
  canvas.style.position = 'fixed'
  canvas.style.left = '-10000px'
  canvas.style.top = '0'
  canvas.style.pointerEvents = 'none'
  host.body.appendChild(canvas)
  return canvas
}

function openStudio(): Studio | null {
  if (studio) return studio
  try {
    const canvas = createStudioCanvas(document)
    const holder: Studio = { canvas, onFrame: null, deck: null as unknown as Deck<OrbitView> }
    holder.deck = new Deck({
      canvas,
      width: PREVIEW_W,
      height: PREVIEW_H,
      // `_setCanvasSize` forces `position: absolute` whenever a height prop is given, unless
      // it is told otherwise here. Fixed keeps the studio out of flow, so it cannot extend
      // the page's scroll extents while it is open.
      style: { position: 'fixed' },
      // Fixed rather than the device's own ratio, so a capture is the same image on every
      // phone and the cache is not silently per-display.
      useDevicePixels: 2,
      views: new OrbitView({ id: 'livery-preview', orthographic: true }),
      controller: false,
      // No clear colour is set: deck's own default is transparent, which is what an overlay
      // renderer has to be — so the capture carries alpha and the tile's background shows
      // through instead of a rectangle of some other grey.
      effects: [PREVIEW_LIGHTING],
      layers: [],
      onAfterRender: () => {
        const cb = holder.onFrame
        holder.onFrame = null
        cb?.()
      },
    })
    studio = holder
    return holder
  } catch {
    // No WebGL, no device, no preview. The flat elevation stands in.
    return null
  }
}

function closeStudio(): void {
  const open = studio
  studio = null
  try {
    open?.deck.finalize()
  } catch {
    // Finalizing a device that never came up is not worth reporting.
  }
  // The off-screen canvas is ours; it goes with the context that was drawing into it.
  open?.canvas.remove()
}

/** Resolves after deck's next actual draw, or gives up. */
function drawnFrame(open: Studio, timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      open.onFrame = null
      resolve(false)
    }, timeoutMs)
    open.onFrame = () => {
      clearTimeout(timer)
      resolve(true)
    }
  })
}

async function renderModel(id: LiveryId, vehicle: LiveryVehicle): Promise<string> {
  if (typeof document === 'undefined') return ''
  const spec = FLEET[vehicle]
  // Awaited, not the map's draw-plain-and-repaint-next-frame path: see the header.
  const painted = await liveryScenegraphReady(spec.model, liveryPaint(id, vehicle), spec.target, id)
  // A STRING here means there was nothing to patch — the as-built livery, or a patch that
  // failed — and handing deck a URL makes the load its business, on its own schedule. The
  // capture would then land on an empty frame and cache a transparent PNG forever. So the
  // glTF is always parsed before the layer is built, whether or not it carries paint.
  const scenegraph =
    typeof painted === 'string'
      ? await load(painted, GLTFLoader, VENDORED_GLB_LOAD_OPTIONS).catch(() => null)
      : painted
  if (!scenegraph) return ''

  const open = openStudio()
  if (!open) return ''
  const { target, zoom } = framing(spec.body)

  open.deck.setProps({
    viewState: {
      target,
      zoom,
      rotationX: PREVIEW_ELEVATION_DEG,
      rotationOrbit: PREVIEW_ORBIT_DEG,
    },
    layers: [
      new ScenegraphLayer<{ id: string }>({
        // Per livery, so deck rebuilds the model instead of diffing a new scenegraph onto
        // the previous one's layer state.
        id: `livery-preview-${vehicle}-${id}`,
        data: [{ id }],
        coordinateSystem: COORDINATE_SYSTEM.CARTESIAN,
        scenegraph: scenegraph as never,
        // Vendored Draco. Required, not tuning — see VENDORED_GLB_LOAD_OPTIONS.
        loadOptions: VENDORED_GLB_LOAD_OPTIONS,
        getPosition: () => [0, 0, 0],
        // Roll 90 for the same reason the map's layers carry it: the glTF is exported Y-up
        // and deck's world is Z-up.
        getOrientation: () => [0, spec.yaw, 90],
        getColor: [255, 255, 255, 255],
        // Life size, in the metres `framing` measured. Deliberately NOT the map's
        // `XCEL40_SIZE_SCALE` — a bus is drawn 1.5× there so it does not smear into the
        // next one on a corridor, which is a legibility decision about a field of vehicles
        // and means nothing to a tile holding exactly one.
        sizeScale: 1,
        ...(spec.usesPbr
          ? {
              _lighting: 'pbr' as const,
              // Without this the stainless renders near-black: a metal has no diffuse to
              // light. See pbrEnvironment.ts.
              _imageBasedLightingEnvironment: ({
                layer,
              }: {
                layer: ScenegraphLayer<{ id: string }>
              }) => r160PbrEnvironment(layer.context.device),
            }
          : {}),
      }),
    ],
  })

  // Two frames: the first is where the layer uploads its buffers and textures, and reading
  // the pixels after it can catch a model that has not finished becoming one.
  if (!(await drawnFrame(open, 8000))) return ''
  open.deck.redraw('livery-preview')
  if (!(await drawnFrame(open, 4000))) return ''
  let url = ''
  try {
    url = open.canvas.toDataURL('image/png')
  } catch {
    return ''
  }
  // A fully transparent capture — nothing drew, for whatever reason — compresses to a few
  // hundred bytes, while any of these vehicles runs to tens of kilobytes. Treating that as a
  // failure is what keeps a blank tile from being cached over a perfectly good elevation.
  return url.length < EMPTY_CAPTURE_BYTES ? '' : url
}

/** Below this a PNG of a 640 × 228 frame is empty, not a vehicle. */
const EMPTY_CAPTURE_BYTES = 2000

/** Serialises renders and closes the studio once nothing is left to draw. */
function enqueue(task: () => Promise<string>): Promise<string> {
  outstanding += 1
  const run = queue.then(task)
  queue = run.then(
    () => undefined,
    () => undefined,
  ).then(() => {
    outstanding -= 1
    if (outstanding === 0) closeStudio()
  })
  return run
}

const models = new Map<string, string>()
const jobs = new Map<string, Promise<string>>()

/**
 * The rendered vehicle, once it exists. Resolves `''` if it cannot be made, which is not an
 * error — it is the signal to keep showing `liveryFlankPreview`.
 */
export function liveryModelPreview(id: LiveryId, vehicle: LiveryVehicle): Promise<string> {
  const key = `${vehicle}:${id}`
  const done = models.get(key)
  if (done !== undefined) return Promise.resolve(done)
  const running = jobs.get(key)
  if (running) return running
  const job = enqueue(() => renderModel(id, vehicle))
    .catch(() => '')
    .then((url) => {
      models.set(key, url)
      jobs.delete(key)
      return url
    })
  jobs.set(key, job)
  return job
}

// ── The flat side elevation, drawn while the model loads ─────────────────────────────

/**
 * The livery's own artwork on a 2D canvas: instant, synchronous, and a real picture of the
 * paint even though it is not a picture of the vehicle. It is what a tile shows until the
 * render lands, and what it keeps if there is no GPU to render with.
 *
 * Two things it has to get right, both consequences of how liveries are authored:
 *
 *   • **It draws at the sheet's own size and scales the RESULT.** Liveries are laid out
 *     against real features and several stamp figures whose floor is a pixel or a font, so a
 *     320 px canvas would not be the same drawing shrunk — it would be a different drawing.
 *   • **The sheet's 8:1 is not the panel's** (`liveryPanelAspect`), and neither is the
 *     tile's, so the elevation is drawn at the body's proportions and letterboxed into
 *     `LIVERY_PREVIEW_ASPECT` rather than stretched to fill it.
 *
 * The RIGHT flank, because `x = w` is the nose on that side — the exporters mirror u on the
 * left island — so it reads nose-forward and handed markings land where the offside wears
 * them. `default` has no paint at all, by design, hence `AS_BUILT_FLANK`.
 */
export function liveryFlankPreview(id: LiveryId, vehicle: LiveryVehicle): string {
  const key = `${vehicle}:${id}`
  const hit = flanks.get(key)
  if (hit !== undefined) return hit
  const url = renderFlank(id, vehicle)
  flanks.set(key, url)
  return url
}

const flanks = new Map<string, string>()

function renderFlank(id: LiveryId, vehicle: LiveryVehicle): string {
  if (typeof document === 'undefined') return ''
  const body = FLEET[vehicle].body
  const out = document.createElement('canvas')
  out.width = PREVIEW_W
  out.height = PREVIEW_H
  const outCtx = out.getContext('2d')
  if (!outCtx) return ''

  // The elevation, at the panel's proportions, centred in the tile.
  const drawW = PREVIEW_W
  const drawH = Math.max(1, Math.round(drawW / liveryPanelAspect(body)))
  const top = Math.round((PREVIEW_H - drawH) / 2)

  const paint = liveryPaint(id, vehicle)
  if (!paint) {
    outCtx.fillStyle = AS_BUILT_FLANK[vehicle]
    outCtx.fillRect(0, top, drawW, drawH)
    return out.toDataURL()
  }

  const sheet = document.createElement('canvas')
  sheet.width = LIVERY_CANVAS_W
  sheet.height = LIVERY_FLANK_H
  const sheetCtx = sheet.getContext('2d')
  if (!sheetCtx) return ''
  // One island, unclipped and untranslated: `drawLivery` in `liveryScenegraph.ts` stacks two
  // of these on one sheet, and a livery fills whatever `w × h` it is handed, so a lone flank
  // is the same call with the bookkeeping removed.
  paint.draw(sheetCtx, LIVERY_CANVAS_W, LIVERY_FLANK_H, 'right', body)

  outCtx.imageSmoothingEnabled = true
  outCtx.imageSmoothingQuality = 'high'
  outCtx.drawImage(sheet, 0, top, drawW, drawH)
  return out.toDataURL()
}

/** Tests and the dev panel: forget every rendered swatch. */
export function resetLiveryPreviewCache(): void {
  flanks.clear()
  models.clear()
  jobs.clear()
}
